/// <reference types="vite/client" />
/**
 * API Client with X-Portal-Source Header Injection
 * REQ-030: Frontend Portal Split - Shared Utils Package
 *
 * This module provides an API client that automatically injects the
 * X-Portal-Source header on all API requests based on the detected portal.
 */

import type { Portal, ApiClient, ApiClientOptions } from './types';
import { extractErrorMessage } from './extract-error-message';

/**
 * Get the API base URL from environment variables.
 * FAIL-FAST: Throws error if env vars are not configured.
 * No hardcoded fallback - ports come from .env.local (see port registry).
 */
function getDefaultApiBaseUrl(): string {
  // Check Vite environment variables at runtime
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) {
    return import.meta.env.VITE_API_URL.replace(/\/api\/v1$/, '');
  }
  throw new Error(
    'VITE_API_BASE_URL or VITE_API_URL must be set in .env.local. ' +
    'Check port registry at ~/apps/local-dev-port-offsets for correct ports.'
  );
}

// Global portal state for standalone usage (set by PortalProvider)
let globalPortal: Portal | null = null;

/**
 * Detect portal from hostname
 * @param hostname - The hostname to detect portal from
 * @returns The detected portal ("APAL" or "HUMANDA")
 */
export function getPortalFromHostname(hostname: string): Portal {
  if (!hostname) {
    return 'APAL';
  }

  const normalizedHostname = hostname.toLowerCase().trim();

  // Check for Humanda hostnames
  if (
    normalizedHostname.startsWith('humanda.') ||
    normalizedHostname.startsWith('humanda-')
  ) {
    return 'HUMANDA';
  }

  // Default to APAL for all other hostnames (including localhost, apal.*, etc.)
  return 'APAL';
}

/**
 * Set the global portal value (called by PortalProvider)
 */
export function setGlobalPortal(portal: Portal): void {
  globalPortal = portal;
}

/**
 * Get the current global portal value
 */
export function getGlobalPortal(): Portal | null {
  return globalPortal;
}

/**
 * Clear the global portal value
 */
export function clearGlobalPortal(): void {
  globalPortal = null;
}

/**
 * Get the current portal (from global state or hostname detection)
 */
function getCurrentPortal(): Portal {
  // First check global portal (set by PortalProvider)
  if (globalPortal) {
    return globalPortal;
  }

  // Fall back to hostname detection
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return getPortalFromHostname(window.location.hostname);
  }

  // Default to APAL
  return 'APAL';
}

/**
 * Create an API client with automatic X-Portal-Source header injection
 * @param portal - The portal to use for the X-Portal-Source header
 * @param options - Optional configuration
 * @returns API client instance
 */
export function createApiClient(
  portal: Portal,
  options: ApiClientOptions = {}
): ApiClient {
  const baseUrl = options.baseUrl ?? getDefaultApiBaseUrl();

  const request = async <T>(
    endpoint: string,
    requestOptions: RequestInit = {}
  ): Promise<T> => {
    const url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`;

    // Merge headers with X-Portal-Source and optional Authorization
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((requestOptions.headers as Record<string, string>) ?? {}),
      'X-Portal-Source': portal,
    };

    // Inject Authorization header if getAuthToken is provided
    if (options.getAuthToken) {
      const token = options.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, {
      ...requestOptions,
      headers,
    });

    if (!response.ok) {
      // 401 Unauthorized: redirect to login with return URL
      if (response.status === 401 && typeof window !== 'undefined') {
        const returnTo = encodeURIComponent(window.location.pathname);
        window.location.href = `/login?returnTo=${returnTo}`;
        // Throw after redirect to halt further execution in the calling code
        throw new Error('Session expired. Redirecting to login...');
      }

      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(extractErrorMessage(error.detail || error.message, `API error: ${response.status}`));
    }

    // Guard: 204 No Content or non-JSON response — return null without parsing
    const contentType = response.headers.get('content-type');
    const hasJsonBody =
      response.status !== 204 &&
      contentType != null &&
      contentType.includes('json');

    if (!hasJsonBody) {
      return null as unknown as T;
    }

    return response.json();
  };

  return {
    request,
    get: <T>(endpoint: string, opts?: RequestInit) =>
      request<T>(endpoint, { ...opts, method: 'GET' }),
    post: <T>(endpoint: string, body?: unknown, opts?: RequestInit) =>
      request<T>(endpoint, { ...opts, method: 'POST', body: JSON.stringify(body) }),
    put: <T>(endpoint: string, body?: unknown, opts?: RequestInit) =>
      request<T>(endpoint, { ...opts, method: 'PUT', body: JSON.stringify(body) }),
    patch: <T>(endpoint: string, body?: unknown, opts?: RequestInit) =>
      request<T>(endpoint, { ...opts, method: 'PATCH', body: JSON.stringify(body) }),
    delete: <T>(endpoint: string, opts?: RequestInit) =>
      request<T>(endpoint, { ...opts, method: 'DELETE' }),
  };
}

/**
 * Standalone API request function that uses the current portal
 * This can be used outside of React components or when PortalProvider
 * has set the global portal value.
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const portal = getCurrentPortal();
  const client = createApiClient(portal);
  return client.request<T>(endpoint, options);
}

/**
 * RequestInit extended with a per-call opt-out flag for the shared
 * `createPortalFetch` 401-navigation behavior.
 *
 * REQ-BVDR-AUTH-BOOTSTRAP-401-LOOP-FIX (PC-011):
 * - `navigateOn401` absent or `true` (DEFAULT) → on a 401 the helper performs
 *   the existing full-page `window.location.href = '/login?returnTo=...'`
 *   navigation, UNCHANGED for all mid-session consumers.
 * - `navigateOn401 === false` → the helper does NOT navigate; it returns the
 *   401 `Response` to the caller for local handling (used by the auth-bootstrap
 *   path so an expired-token bootstrap does not enter a reload loop).
 *
 * The custom flag is STRIPPED before delegating to native `fetch` so it never
 * leaks into the `RequestInit` passed to the platform fetch.
 */
export type PortalFetchInit = RequestInit & { navigateOn401?: boolean };

/**
 * A portal-aware fetch wrapper. Behaves like `fetch` but additionally accepts
 * the `navigateOn401` opt-out flag on its init argument.
 */
export type PortalFetch = (
  input: RequestInfo | URL,
  init?: PortalFetchInit
) => Promise<Response>;

/**
 * Create a fetch wrapper that injects X-Portal-Source header
 * Use this to wrap existing fetch calls
 */
export function createPortalFetch(portal: Portal): PortalFetch {
  return async (input: RequestInfo | URL, init?: PortalFetchInit): Promise<Response> => {
    // Strip the custom `navigateOn401` flag before delegating to native fetch
    // so it never leaks into the platform RequestInit (no fetch/TS friction).
    const { navigateOn401, ...fetchInit } = init ?? {};

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((fetchInit.headers as Record<string, string>) ?? {}),
      'X-Portal-Source': portal,
    };

    const response = await fetch(input, {
      ...fetchInit,
      headers,
    });

    // 401 Unauthorized: redirect to login with return URL.
    // DEFAULT (flag absent or true) is UNCHANGED — preserves mid-session
    // navigation for all ~23 non-bootstrap consumers. Only an explicit
    // `navigateOn401: false` opt-out suppresses the navigation.
    if (
      response.status === 401 &&
      navigateOn401 !== false &&
      typeof window !== 'undefined'
    ) {
      const returnTo = encodeURIComponent(window.location.pathname);
      window.location.href = `/login?returnTo=${returnTo}`;
    }

    return response;
  };
}
